/**
 * Pure helpers for the auth/signup funnel. Extracted from
 * `components/analytics/auth-funnel-sync.tsx` so the real logic (new-account
 * heuristic, signup dedup, payload building) is unit-testable in the node-env
 * Vitest setup without rendering React.
 */
import type { AnalyticsEventMap } from "./events";
import type { AttributionData } from "./types";

/**
 * A login counts as a fresh signup only if the account is younger than this.
 * Widened 5min → 30min so `/auth/me` latency and minor clock differences don't
 * drop signups; a returning account's `created_at` is days/weeks old, so a
 * generous window carries no false-positive risk. The authoritative signal is
 * the server's `is_new_account` flag (threaded via StoredAuth) — this heuristic
 * is the fallback when that flag is absent.
 */
export const NEW_ACCOUNT_WINDOW_MS = 30 * 60 * 1000;

/**
 * Tolerate a client clock running behind the server: otherwise a brand-new
 * account's `created_at` looks slightly in the future and the signup is missed.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

const SIGNUP_FIRED_PREFIX = "tenbagger.analytics.signup_fired.";

/**
 * Parse a server `created_at` into epoch ms, defensively. Returns null when
 * unparseable. Handles the shapes the backend might send:
 *   - ISO-8601 WITH a timezone (`…Z` / `…+09:00`) — used as-is.
 *   - ISO or space-separated WITHOUT a timezone — assumed UTC. A tz-naive string
 *     would otherwise be parsed in the browser's local zone; in KST (+9) that
 *     throws the age off by 9h — past the window — so every signup misfires.
 *   - A numeric epoch in seconds or milliseconds.
 */
export function parseCreatedAt(createdAt: string): number | null {
	const trimmed = createdAt.trim();
	if (trimmed === "") return null;

	if (/^\d+$/.test(trimmed)) {
		const n = Number(trimmed);
		// ≤11 digits ≈ seconds (covers dates until year 2286) → scale to ms.
		const ms = trimmed.length <= 11 ? n * 1000 : n;
		return Number.isFinite(ms) ? ms : null;
	}

	const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
	const normalized = hasTimezone ? trimmed : `${trimmed.replace(" ", "T")}Z`;
	const ms = Date.parse(normalized);
	return Number.isNaN(ms) ? null : ms;
}

/**
 * True if `created_at` indicates a brand-new account — within
 * `NEW_ACCOUNT_WINDOW_MS`, allowing `CLOCK_SKEW_TOLERANCE_MS` of client-clock
 * skew. Unparseable timestamps fail CLOSED to "not new". Prefer the server's
 * `is_new_account` flag where available; this is the fallback heuristic.
 */
export function isNewAccount(
	createdAt: string,
	now: number = Date.now(),
): boolean {
	const created = parseCreatedAt(createdAt);
	if (created === null) return false;
	const age = now - created;
	return age >= -CLOCK_SKEW_TOLERANCE_MS && age < NEW_ACCOUNT_WINDOW_MS;
}

/** Whether `sign_up` was already emitted for this account (cross-session, per-device). */
export function hasFiredSignup(userId: string): boolean {
	if (typeof window === "undefined") return true; // never double-fire on SSR
	return window.localStorage.getItem(SIGNUP_FIRED_PREFIX + userId) === "1";
}

/** Mark `sign_up` as emitted for this account so it is exactly-once. */
export function markSignupFired(userId: string): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(SIGNUP_FIRED_PREFIX + userId, "1");
	} catch {
		// best-effort (private mode / quota)
	}
}

/**
 * Build the `sign_up` payload, null-filling every attribution field so the event
 * always carries a consistent schema even when first-touch capture was blocked.
 */
export function buildSignupPayload(
	referredByCode: string | null,
	attribution: AttributionData | null,
): AnalyticsEventMap["sign_up"] {
	return {
		method: "email",
		referred_by_code: referredByCode,
		utm_source: attribution?.utm_source ?? null,
		utm_medium: attribution?.utm_medium ?? null,
		utm_campaign: attribution?.utm_campaign ?? null,
		utm_term: attribution?.utm_term ?? null,
		utm_content: attribution?.utm_content ?? null,
		gclid: attribution?.gclid ?? null,
		fbclid: attribution?.fbclid ?? null,
		referrer: attribution?.referrer ?? null,
		landing_path: attribution?.landing_path ?? "",
		captured_at: attribution?.captured_at ?? "",
	};
}
